import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { ChartConfiguration, ChartData } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { FroelingHistoryPoint } from '../version.service';
import { chartPalette, timeTooltipTitle, lineLegendLabels, dragZoom, timeAxisTick } from '../shared/chart.utils';

@Component({
  selector: 'app-heizung-chart',
  template: `
    <div style="position:relative;height:clamp(200px,55vh,700px);">
      <canvas baseChart
        [data]="chartData"
        [options]="chartOptions"
        type="line">
      </canvas>
      <button *ngIf="zoomed" class="btn btn-sm btn-outline-secondary"
              style="position:absolute;top:4px;right:4px;font-size:.65rem;padding:1px 6px;opacity:.85;z-index:2;"
              (click)="resetZoom()">↩ Reset Zoom</button>
    </div>
  `,
})
export class HeizungChartComponent implements OnChanges {
  @ViewChild(BaseChartDirective) chartDirective?: BaseChartDirective;
  @Input() history: FroelingHistoryPoint[] = [];
  @Input() darkMode = false;
  @Input() windowMs = 24 * 3600 * 1000;

  zoomed = false;
  chartData: ChartData<'line'> = { datasets: [] };
  chartOptions: ChartConfiguration<'line'>['options'] = {};

  resetZoom(): void {
    (this.chartDirective?.chart as any)?.resetZoom();
    this.zoomed = false;
  }

  ngOnChanges(): void {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    const pts = this.history;
    const xMax = Date.now();
    const xMin = xMax - this.windowMs;

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: true, labels: lineLegendLabels(textColor) },
        tooltip: {
          callbacks: {
            title: timeTooltipTitle,
            label: (item) => {
              if ((item.dataset as any).yAxisID === 'y1') {
                return ` ${item.dataset.label}: ${Math.round(item.parsed.y ?? 0)} %`;
              }
              return ` ${item.dataset.label}: ${(item.parsed.y ?? 0).toFixed(1)} °C`;
            },
          },
        },
        zoom: dragZoom(() => { this.zoomed = true; }),
      },
      scales: {
        x: {
          type: 'linear',
          min: xMin,
          max: xMax,
          ticks: {
            color: textColor,
            maxTicksLimit: 9,
            callback: timeAxisTick(xMin, xMax),
          },
          grid: { color: gridColor },
        },
        y: {
          ticks: {
            color: textColor,
            callback: (val) => `${(val as number).toFixed(0)} °C`,
          },
          grid: { color: gridColor },
          title: { display: true, text: 'Temperatur (°C)', color: textColor, font: { size: 11 } },
        },
        y1: {
          position: 'right',
          min: 0,
          max: 100,
          ticks: {
            color: textColor,
            callback: (val) => `${val} %`,
            maxTicksLimit: 6,
          },
          grid: { drawOnChartArea: false },
          title: { display: true, text: 'Ladezustand (%)', color: textColor, font: { size: 11 } },
        },
      },
      elements: {
        point: { radius: 0, hoverRadius: 3 },
        line: { borderWidth: 1.5, tension: 0.6 },
      },
    };

    const datasets: any[] = [
      {
        label: '🔥 Kessel',
        data: pts.map(p => ({ x: p.ts, y: p.boilerTempC })),
        borderColor: '#dc3545',
        backgroundColor: 'transparent',
        fill: false,
      },
      {
        label: '🛢️ Puffer oben',
        data: pts.map(p => ({ x: p.ts, y: p.bufferTopTempC })),
        borderColor: '#0d6efd',
        backgroundColor: 'transparent',
        fill: false,
      },
      {
        label: '🛢️ Puffer unten',
        data: pts.map(p => ({ x: p.ts, y: p.bufferBottomTempC })),
        borderColor: '#6ea8fe',
        backgroundColor: 'transparent',
        fill: false,
        borderDash: [4, 3],
      },
      {
        label: '🚿 Warmwasser',
        data: pts.map(p => ({ x: p.ts, y: p.hotWaterTempC })),
        borderColor: '#0dcaf0',
        backgroundColor: 'transparent',
        fill: false,
      },
      {
        label: '🌤️ Außen',
        data: pts.filter(p => p.outdoorTempC != null).map(p => ({ x: p.ts, y: p.outdoorTempC })),
        borderColor: '#6c757d',
        backgroundColor: 'transparent',
        fill: false,
        borderDash: [6, 4],
      },
    ];

    if (pts.some(p => p.solarThermalTempC > 0)) {
      datasets.push({
        label: '☀️ Solarthermie',
        data: pts.map(p => ({ x: p.ts, y: p.solarThermalTempC })),
        borderColor: '#ffc107',
        backgroundColor: 'transparent',
        fill: false,
      });
    }

    if (pts.some(p => p.exhaustTempC > 0)) {
      datasets.push({
        label: '💨 Abgas',
        data: pts.map(p => ({ x: p.ts, y: p.exhaustTempC })),
        borderColor: '#fd7e14',
        backgroundColor: 'transparent',
        fill: false,
        borderDash: [4, 3],
      });
    }

    const hcColors = ['#198754', '#6610f2', '#d63384', '#20c997'];
    for (let i = 0; i < (pts[0]?.heatingCircuits?.length ?? 0); i++) {
      const name = pts[0].heatingCircuits[i]?.name ?? `HK${i + 1}`;
      datasets.push({
        label: `🌡️ ${name}`,
        data: pts.filter(p => p.heatingCircuits?.[i]).map(p => ({ x: p.ts, y: p.heatingCircuits[i].flowTempActualC })),
        borderColor: hcColors[i % hcColors.length],
        backgroundColor: 'transparent',
        fill: false,
      });
    }

    datasets.push({
      label: '🛢️ Puffer %',
      data: pts.map(p => ({ x: p.ts, y: p.bufferChargePct })),
      borderColor: '#a855f7',
      backgroundColor: 'transparent',
      fill: false,
      yAxisID: 'y1',
      borderDash: [4, 3],
    });

    this.chartData = { datasets };
  }
}
